import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(email, password, name);
      toast.success(`Welcome, ${u.name}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
          alt="Onboarding cohort"
        />
        <div className="absolute inset-0" style={{backgroundColor:'color-mix(in srgb, var(--brand-primary) 50%, transparent)'}} />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="label-eyebrow text-white/70 mb-3">A new chapter</div>
          <div className="font-display text-4xl leading-tight max-w-md">
            Build the career you promised yourself.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-[color:var(--brand-bg)]">
        <div className="w-full max-w-md">
          <Link to="/" className="label-eyebrow" data-testid="back-home">← Back home</Link>
          <h1 className="font-display text-4xl mt-6 mb-2">Create account</h1>
          <p className="text-neutral-500 mb-10">Your onboarding starts the moment you sign up.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">Full name</label>
              <input
                required value={name} onChange={(e)=>setName(e.target.value)}
                data-testid="register-name"
                className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Work email</label>
              <input
                type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                data-testid="register-email"
                className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                data-testid="register-password"
                className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              />
            </div>
            <button disabled={busy} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors disabled:opacity-60" data-testid="register-submit">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>

          <div className="mt-8 text-sm text-neutral-500">
            Already onboarded? <Link to="/login" className="text-[var(--brand-primary)] link-underline" data-testid="link-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
